import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { fmtNZD, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BulkDeleteDialog from "@/components/BulkDeleteDialog";
import NumberInput from "@/components/NumberInput";

export default function PurchaseOrdersTab() {
  const { project } = useOutletContext();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ supplier_name: "", lines: [{ description: "", qty: 1, rate: 0, total: 0 }], notes: "" });
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const isAdmin = user?.role === "Admin";

  async function load() {
    const { data } = await api.get("/purchase-orders");
    const filtered = data.filter(p => p.project_id === project.id);
    setList(filtered);
    setSelected(prev => new Set([...prev].filter(id => filtered.some(d => d.id === id))));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  function setLine(i, k, v) {
    setForm(f => {
      const lines = [...f.lines]; lines[i] = { ...lines[i], [k]: v };
      lines[i].total = (parseFloat(lines[i].qty)||0) * (parseFloat(lines[i].rate)||0);
      return { ...f, lines };
    });
  }
  async function create(e) {
    e.preventDefault();
    await api.post("/purchase-orders", { ...form, project_id: project.id, project_name: project.name });
    setForm({ supplier_name: "", lines: [{ description: "", qty: 1, rate: 0, total: 0 }], notes: "" });
    setOpen(false); load();
  }

  function toggleAll() {
    if (selected.size === list.length) setSelected(new Set());
    else setSelected(new Set(list.map(p => p.id)));
  }
  function toggleOne(id) {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  }
  const selectedList = list.filter(p => selected.has(p.id));
  const totalAmount = selectedList.reduce((s, p) => s + (parseFloat(p.total_ex || 0)), 0);

  async function bulkConfirm({ confirm_text }) {
    const r = await api.post("/purchase-orders/bulk-delete", { ids: Array.from(selected), confirm_text });
    setMsg(`Deleted ${r.data.soft_deleted} purchase orders. Restorable from Projects → Deleted items for 30 days.`);
    setSelected(new Set());
    await load();
  }

  return (
    <div className="space-y-3" data-testid="pos-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-xl">Purchase Orders</h2>
        <div className="flex gap-2">
          {isAdmin && selected.size > 0 && (
            <button className="btn-ghost text-red-400 text-sm" data-testid="btn-bulk-delete-pos"
                    onClick={() => setBulkOpen(true)}>
              Delete selected ({selected.size})
            </button>
          )}
          <button className="btn-silver" onClick={() => setOpen(!open)}>+ New PO</button>
        </div>
      </div>
      {msg && <div className="text-green-400 text-sm" data-testid="po-list-msg">{msg}</div>}
      {open && (
        <form onSubmit={create} className="card space-y-2">
          <input className="input" placeholder="Supplier name" value={form.supplier_name} onChange={e=>setForm({...form,supplier_name:e.target.value})}/>
          {form.lines.map((l, i) => (
            <div key={i} className="grid grid-cols-4 gap-2">
              <input className="input" placeholder="Description" value={l.description} onChange={e=>setLine(i, "description", e.target.value)}/>
              <NumberInput className="input" value={l.qty} onChange={e=>setLine(i, "qty", e.target.value)}/>
              <NumberInput className="input" money value={l.rate} onChange={e=>setLine(i, "rate", e.target.value)}/>
              <div className="self-center text-silver">{fmtNZD(l.total)}</div>
            </div>
          ))}
          <button type="button" className="btn-ghost text-sm" onClick={()=>setForm(f=>({...f,lines:[...f.lines,{description:"",qty:1,rate:0,total:0}]}))}>+ Line</button>
          <div className="flex gap-2"><button className="btn-silver">Create</button><button type="button" className="btn-ghost" onClick={()=>setOpen(false)}>Cancel</button></div>
        </form>
      )}
      <div className="card">
        <table className="table"><thead><tr>
          {isAdmin && (
            <th className="w-8"><input type="checkbox" data-testid="po-select-all"
              checked={list.length > 0 && selected.size === list.length}
              onChange={toggleAll} aria-label="Select all"/></th>
          )}
          <th>No.</th><th>Supplier</th><th>Total</th><th>Status</th><th></th></tr></thead>
          <tbody>{list.map(p => (
            <tr key={p.id} data-testid={`po-${p.id}`}>
              {isAdmin && (
                <td><input type="checkbox" data-testid={`po-select-${p.id}`}
                  checked={selected.has(p.id)} onChange={() => toggleOne(p.id)}/></td>
              )}
              <td>{p.number}</td><td>{p.supplier_name}</td><td>{fmtNZD(p.total_ex)}</td>
              <td><span className="badge badge-grey">{p.status}</span></td>
              <td><a className="hub-link text-xs" href={`${BACKEND_URL}/api/purchase-orders/${p.id}/pdf`} target="_blank" rel="noreferrer">PDF</a></td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={isAdmin ? "6" : "5"} className="opacity-60">No purchase orders yet.</td></tr>}
          </tbody></table>
      </div>
      <BulkDeleteDialog open={bulkOpen} onClose={() => setBulkOpen(false)}
        count={selected.size} items={selectedList.map(p => `${p.number} · ${p.supplier_name} · ${fmtNZD(p.total_ex)}`)}
        totalAmount={totalAmount} hasApproved={false}
        onConfirm={bulkConfirm}
        labels={{ entity: "purchase orders", noun: "purchase order" }}/>
    </div>
  );
}
